/**
 * The case BRIEF, reduced to what the collapsed header renders (§3.4-ZERO).
 *
 * > **Operator, verbatim 2026-07-29:** *"the most important part of this case
 * > header/summary is exactly the case summary updated by m-case-updated
 * > mission … This calls for a compact header that is shown in collapsed
 * > format (like 2-lines most)."*
 *
 * The producer chain has been complete and live for days — the mission runs,
 * the harness writes `case-brief.json`, `case.context` serves it. **No SPA
 * surface ever fetched it.** That is why the operator kept seeing "the old
 * header": the summary was never missing, it was never *read*.
 *
 * Everything here is pure so the three states that matter are testable without
 * a gateway — and they matter because two of them look identical if you are
 * careless.
 */

import type { CaseBrief, CaseBriefMeta, LabelMap } from "@/types/gateway";

/**
 * What the header can say about the summary itself.
 *
 * - `absent`  — no brief file at all. The mission has not run for this case.
 * - `empty`   — a brief exists and is deliberately EMPTY. A case with no
 *               documents has nothing to summarise, so the harness writes `{}`
 *               rather than inventing prose. **This is CORRECT, not breakage**,
 *               and it is a different sentence to the lawyer than `absent`.
 * - `present` — there is a synopsis to show.
 */
export type BriefPresence = "absent" | "empty" | "present";

export function briefPresence(brief: CaseBrief | undefined): BriefPresence {
  if (!brief || typeof brief !== "object") return "absent";
  // Empty is judged on the SYNOPSIS, not on the object having zero keys: a
  // brief carrying `risks` but no synopsis still has nothing for line 1, and
  // pretending otherwise would render an empty string where a sentence goes.
  const text = brief.synopsis?.text;
  return typeof text === "string" && text.trim().length > 0 ? "present" : "empty";
}

/**
 * The staleness marker — THREE states, and the third is the one that gets lost.
 *
 * `stale: false` ⇒ verified fresh ⇒ say nothing (a "fresh" badge is noise).
 * `stale: true`  ⇒ a quiet "desatualizado".
 * **key ABSENT** ⇒ the digest could not be recomputed ⇒ a DISTINCT quiet
 * "não foi possível verificar". **Not silence.**
 *
 * Rendering nothing for both `false` and absent is the defect: it tells the
 * lawyer the summary is current on exactly the cases where nobody can say. That
 * is the reassuring-direction lie this whole track has been correcting, and it
 * is why the `⟳ Atualizar` button sits adjacent — *unverifiable* is a strictly
 * better reason to refresh than *stale*.
 */
/**
 * ⚠ FOUR states, not three. `no-brief` was added 2026-07-30 after an
 * operator-reported bug: `!meta` used to return `"unverifiable"`, so a case that
 * had simply never been analysed announced *"não foi possível verificar"* — a
 * claim that something went wrong with VERIFICATION, when the truth is there is
 * nothing to verify. Two genuinely different situations were wearing one answer.
 *
 * The docstring above always described the LAST branch correctly (a brief exists,
 * its `stale` key is missing ⇒ the digest could not be recomputed). The FIRST
 * branch was a different case that had been folded into it — the exact
 * state-collapsing defect this whole track has been unpicking, committed here by
 * me.
 */
export type StalenessMark = "none" | "stale" | "unverifiable" | "no-brief";

export function stalenessMark(meta: CaseBriefMeta | undefined): StalenessMark {
  // NO BRIEF AT ALL. Staleness is meaningless — there is no summary whose
  // freshness could be in question. Distinct from `none` (verified fresh) so a
  // caller can never render a freshness claim about a summary that is absent.
  if (!meta) return "no-brief";
  if (meta.stale === true) return "stale";
  if (meta.stale === false) return "none";
  // A brief EXISTS and its `stale` key is missing ⇒ the digest could not be
  // recomputed. This — and only this — is "could not verify".
  return "unverifiable";
}

/** The two marks that have something to say. `none` and `no-brief` are both
 *  silent, for different reasons, and naming that here stops a caller
 *  re-deriving the set with a `!==` that quietly admits a fourth value. */
export function stalenessIsVisible(mark: StalenessMark): mark is "stale" | "unverifiable" {
  return mark === "stale" || mark === "unverifiable";
}

/** i18n key for a mark. `none` has no key by construction — it renders nothing. */
export function stalenessI18nKey(mark: "stale" | "unverifiable"): string {
  return mark === "stale"
    ? "caseWorkspace.brief.stale"
    : "caseWorkspace.brief.unverifiable";
}

/**
 * Collapse `incomplete[]` into one generic caveat.
 *
 * ⚠ **The default branch is the entire point.** `incomplete[]` is a CLOSED
 * vocabulary today, and the ruling that keeps it closed rests on consumers
 * having a catch-all: without one, the next token the backend adds renders
 * nothing and vanishes silently — the failure the ruling claims to prevent,
 * merely deferred. So this returns `true` for ANY non-empty array, including
 * tokens it has never seen.
 */
export function hasIncompleteData(incomplete: readonly string[] | undefined): boolean {
  return Array.isArray(incomplete) && incomplete.length > 0;
}

/* ---------- The heading fields: label resolution + the tri-states ---------- */

/**
 * Pick the display string for a `LabelMap`.
 *
 * ⚠ **The reason this exists rather than `label[locale]` at each call site:**
 * only `pt` is required by the contract, so indexing a locale blindly yields
 * `undefined` — and `{...}` rendered into JSX is how this codebase already
 * shipped `[object Object]` to the operator once. One resolver, one place to be
 * wrong.
 *
 * Order: **requested locale → `pt` → `en` → `es` → `fallback`.** `pt` before the
 * others because it is the only guaranteed member, so it is the cheapest true
 * answer rather than a lucky one.
 *
 * `fallback` is where the operator's original "self-describing field names"
 * instinct survives: pass the field's `key`, and an unmapped locale degrades to
 * `processo` rather than to a blank or a raw `[object Object]`.
 */
export function pickLabel(
  label: LabelMap | undefined,
  locale: string | undefined,
  fallback = "",
): string {
  if (!label || typeof label !== "object") return fallback;
  const lang = (locale ?? "").slice(0, 2).toLowerCase();
  // Narrowed to the contract's three keys rather than indexed by a raw string:
  // an arbitrary `locale` must not be able to reach into the object.
  const requested = lang === "pt" ? label.pt : lang === "en" ? label.en : lang === "es" ? label.es : undefined;
  const ordered = [requested, label.pt, label.en, label.es];
  for (const candidate of ordered) {
    if (typeof candidate === "string" && candidate.trim().length > 0) return candidate;
  }
  return fallback;
}

/**
 * The third state, made explicit so it cannot be lost in a `?:`.
 *
 * `isOurClient` and `segredoDeJustica` are **TRI-STATE BY OMISSION**: absent
 * means the producer REFUSED to determine it, not that the answer is no. Both
 * matter in both directions — a fabricated `false` on the client's own party
 * tells a lawyer they act against their own client; a fabricated
 * not-under-seal changes how they handle the file.
 *
 * Returning a union rather than `boolean | undefined` means a consumer must
 * name the third case to compile past a `switch`, instead of falling into a
 * two-branch ternary that silently treats undetermined as "no".
 */
export type TriState = "yes" | "no" | "unknown";

export function triState(v: boolean | undefined): TriState {
  if (v === true) return "yes";
  if (v === false) return "no";
  return "unknown";
}
